import Image from 'next/image';

import { Skeleton } from '@mantine/core';
import { Supplier } from '@scaleup/models';
import {
  IconAward,
  IconCertificate,
  IconLink,
  IconPackages,
  IconTagsFilled,
  IconTrophy,
} from '@tabler/icons-react';
import { useGetSupplierQuery } from '../../api';
import {
  Conditional,
  Loading,
  Modal,
  SupplierAvatar,
  Tabs,
  Tag,
} from '../../ui';
import {
  isNilString,
  safeLink,
  unixToHumanReadable,
  unixToYear,
} from '../../utils';

const safeDate = (date?: string | number) => {
  if (!date) {
    return false;
  }

  if (typeof date === 'string') {
    return !isNilString(date) && date !== '0';
  }

  if (date === 0) {
    return false;
  }

  return true;
};

const MainContact = ({ supplier }: { supplier: Supplier }) => {
  const mainContact = supplier.supplierData.mainContact;

  if (!mainContact) {
    return null;
  }

  if (Object.values(mainContact).every((val) => isNilString(val))) {
    return null;
  }

  return (
    <div className="flex items-start gap-4 p-6 bg-[#F1F3FF] rounded-[2rem]">
      <div className="bg-[#DCE1FF] p-4 rounded-full">
        <Image
          src="/icons/user.svg"
          width={24}
          height={24}
          alt="main contact icon"
        />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xl font-bold text-text-primary">
          Main Contact
        </span>

        <div className="flex gap-96">
          <div className="flex flex-col gap-2">
            <Conditional condition={!isNilString(mainContact.firstName)}>
              <div className="flex flex-col">
                <span className="text-text-primary font-bold">First Name</span>
                <span className="text-text-secondary">
                  {mainContact.firstName}
                </span>
              </div>
            </Conditional>
            <Conditional condition={!isNilString(mainContact.title)}>
              <div className="flex flex-col">
                <span className="text-text-primary font-bold">Title</span>
                <span className="text-text-secondary">{mainContact.title}</span>
              </div>
            </Conditional>
            <Conditional condition={!isNilString(mainContact.email)}>
              <div className="flex flex-col">
                <span className="text-text-primary font-bold">Email</span>
                <span className="text-text-secondary">{mainContact.email}</span>
              </div>
            </Conditional>
          </div>
          <div className="flex flex-col gap-2">
            <Conditional condition={!isNilString(mainContact.lastName)}>
              <div className="flex flex-col">
                <span className="text-text-primary font-bold">Last Name</span>
                <span className="text-text-secondary">
                  {mainContact.lastName}
                </span>
              </div>
            </Conditional>
            <Conditional
              condition={!isNilString(supplier.supplierData.mainContact.phone)}
            >
              <div className="flex flex-col">
                <span className="text-text-primary font-bold">Phone</span>
                <span className="text-text-secondary">
                  {supplier.supplierData.mainContact.phone}
                </span>
              </div>
            </Conditional>
          </div>
        </div>
      </div>
    </div>
  );
};

const AnnualGrowthRate = ({ supplier }: { supplier: Supplier }) => {
  if (!supplier.supplierData.annualGrowthRate) {
    return null;
  }

  if (supplier.supplierData.annualGrowthRate <= 0) {
    return null;
  }

  return (
    <div className="flex items-start gap-4 p-6 bg-secondary-25 rounded-[2rem]">
      <div className="bg-secondary-50 p-4 rounded-full">
        <Image
          src="/icons/growth.svg"
          width={24}
          height={24}
          alt="growth icon"
        />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xl font-bold">Annual Growth Rate</span>
        <span>{supplier?.supplierData.annualGrowthRate}</span>
      </div>
    </div>
  );
};

const NaicsCodes = ({ supplier }: { supplier: Supplier }) => {
  if (!supplier.supplierData.naics) {
    return null;
  }

  if (supplier.supplierData.naics.length < 1) {
    return null;
  }

  return (
    <div className="flex items-start gap-4 p-6 bg-[#FFF3F0] rounded-[2rem]">
      <div className="bg-[#FFE1DA] p-4 rounded-full">
        <IconTagsFilled width={24} height={24} color="#ff866a" />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xl font-bold">NAICS Codes</span>
        <div className="flex gap-2 flex-wrap">
          {supplier.supplierData.naics.map(
            (naic: { code: string; label: string }) => (
              <div
                key={naic.code}
                className="px-4 py-1 rounded-[2rem] bg-[#FF866A] text-white"
              >
                {naic.code}: {naic.label}
              </div>
            )
          )}
        </div>
      </div>
    </div>
  );
};

const BusinessActivities = ({ supplier }: { supplier: Supplier }) => {
  return (
    <div className="flex items-start gap-4 p-6 bg-primary-25 rounded-[2rem]">
      <div className="bg-primary-50 p-4 rounded-full">
        <IconPackages color="#4fb3cf" size={24} />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xl font-bold">Business Activities</span>
        <span className="text-text-secondary">
          {supplier.supplierData.primaryBusinessActivity}
        </span>
        <span className="text-text-primary font-[500]">
          Product & Service Offerings
        </span>
        <div className="flex gap-2 flex-wrap">
          {supplier.supplierData.productsAndServices.map((it) => (
            <div className="px-4 py-1 rounded-[2rem] bg-primary-50 text-primary-700">
              {it}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const Industry = ({ supplier }: { supplier: Supplier }) => {
  const getEstablishedDate = () => {
    if (!isNilString(supplier.supplierData.establishmentDate)) {
      return unixToYear(Number(supplier.supplierData.establishmentDate));
    }

    if (supplier.supplierData.establishmentYear) {
      return supplier.supplierData.establishmentYear;
    }

    return null;
  };

  const getYearsInBusiness = () => {
    const currentYear = new Date().getFullYear();
    if (!isNilString(supplier.supplierData.establishmentDate)) {
      return (
        currentYear -
        unixToYear(Number(supplier.supplierData.establishmentDate))
      );
    }

    if (supplier.supplierData.establishmentYear) {
      return currentYear - Number(supplier.supplierData.establishmentYear);
    }

    return null;
  };

  return (
    <div className="flex items-start gap-4 p-6 bg-secondary-25 rounded-[2rem]">
      <div className="bg-secondary-50 p-4 rounded-full">
        <Image
          src="/icons/building.svg"
          width={24}
          height={24}
          alt="industry icon"
        />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xl font-bold text-text-primary">Industry</span>
        <span className="text-text-secondary">
          {supplier?.supplierData?.industry}
        </span>
        <div className="flex gap-96">
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <span className="text-text-primary font-bold">
                Headquarter City
              </span>
              <span className="text-text-secondary">{supplier?.city}</span>
            </div>
            <Conditional condition={Boolean(getEstablishedDate())}>
              <div className="flex flex-col">
                <span className="text-text-primary font-bold">Established</span>
                <span className="text-text-secondary">
                  {getEstablishedDate()}
                </span>
              </div>
            </Conditional>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-col">
              <span className="text-text-primary font-bold">
                Headquarter State
              </span>
              <span className="text-text-secondary">{supplier.state}</span>
            </div>
            <Conditional condition={Boolean(getYearsInBusiness())}>
              <div className="flex flex-col">
                <span className="text-text-primary font-bold">
                  Years in Business
                </span>
                <span className="text-text-secondary">
                  {getYearsInBusiness()}
                </span>
              </div>
            </Conditional>
          </div>
        </div>
        <Conditional
          condition={(supplier.supplierData.geographiesServed || []).length > 0}
        >
          <span className="text-text-primary font-bold">
            Geographies Served
          </span>
          <div className="flex gap-2 flex-wrap">
            {(supplier.supplierData.geographiesServed || []).map((it) => (
              <div className="px-4 py-1 rounded-[2rem] bg-secondary-50 text-secondary-700">
                {it}
              </div>
            ))}
          </div>
        </Conditional>
      </div>
    </div>
  );
};

const URLs = ({ supplier }: { supplier: Supplier }) => {
  return (
    <div className="flex items-start gap-4 p-6 bg-primary-25 rounded-[2rem]">
      <div className="bg-primary-50 p-4 rounded-full">
        <IconLink color="#4fb3cf" width={24} height={24} stroke={2.5} />
      </div>
      <div className="flex flex-col gap-2">
        <span className="text-xl font-bold">URLs</span>
        <Conditional
          condition={!isNilString(supplier.supplierData.capabilitySheet?.url)}
        >
          <div className="flex gap-96">
            <div className="flex flex-col gap-2">
              <div className="flex flex-col">
                <span className="text-text-primary font-bold">
                  Supplier Capability Statement URL
                </span>
                <span>
                  <a
                    className="text-primary-700"
                    href={safeLink(supplier.supplierData.capabilitySheet?.url)}
                    target="_blank"
                    rel="noopener"
                  >
                    {supplier.supplierData.capabilitySheet?.url}
                  </a>
                </span>
              </div>
            </div>
          </div>
        </Conditional>
        <div className="">
          <span>Websites</span>
          <div className="flex gap-2">
            <a
              className="px-4 py-1 rounded-[2rem] bg-primary-50 text-primary-700"
              href={safeLink(supplier.website)}
              target="_blank"
            >
              {supplier.website}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

const hasCertifications = (supplier?: Supplier) =>
  [
    ...(supplier?.supplierData?.industryCertifications || []),
    ...(supplier?.supplierData?.demographicCertifications || []),
  ].length > 0;

const Certifications = ({ supplier }: { supplier: Supplier }) => {
  return (
    <div className="flex items-start gap-4 p-6 bg-[#F1F3FF] rounded-[2rem] w-full">
      <div className="bg-[#DCE1FF] p-4 rounded-full">
        <IconCertificate size={24} color="#7189FF" />
      </div>
      <div className="flex flex-col gap-2 ">
        <span className="text-xl font-bold text-text-primary">
          Certifications
        </span>

        <div className="divide-y divide-[#9CABFF] w-full">
          {[
            ...(supplier.supplierData.industryCertifications || []),
            ...(supplier.supplierData.demographicCertifications || []),
          ].map((it) => {
            return (
              <div className={`flex gap-24 justify-between py-4 !w-full`}>
                <div className="w-1/2 min-w-[200px] flex flex-col gap-2">
                  <div className="flex flex-col">
                    <span className="text-text-primary font-bold">
                      Certification Name
                    </span>
                    <span className="text-text-secondary">{it.name}</span>
                  </div>
                  <Conditional condition={!isNilString(it.agency)}>
                    <div className="flex flex-col">
                      <span className="text-text-primary font-bold">
                        Certification Agency
                      </span>
                      <span className="text-text-secondary">{it.agency}</span>
                    </div>
                  </Conditional>
                  <Conditional condition={!isNilString(it.type)}>
                    <div className="flex flex-col">
                      <span className="text-text-primary font-bold">
                        Certification Type
                      </span>
                      <span className="text-text-secondary">{it.type}</span>
                    </div>
                  </Conditional>
                </div>
                <div className="w-1/2 min-w-[200px] flex flex-col gap-2">
                  <Conditional condition={safeDate(it.date)}>
                    <div className="flex flex-col">
                      <span className="text-text-primary font-bold">
                        Certification Date
                      </span>
                      <span className="text-text-secondary">
                        {unixToHumanReadable(it.date || 0)}
                      </span>
                    </div>
                  </Conditional>
                  <Conditional condition={!isNilString(it.number)}>
                    <div className="flex flex-col">
                      <span className="text-text-primary font-bold">
                        Certification Number
                      </span>
                      <span className="text-text-secondary">{}</span>
                    </div>
                  </Conditional>
                  <Conditional condition={false}>
                    <div className="flex flex-col">
                      <span className="text-text-primary font-bold">
                        Certification Status
                      </span>
                      <span className="text-text-secondary">{it.status}</span>
                    </div>
                  </Conditional>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

const CertificationTags = ({ supplier }: { supplier: Supplier }) => {
  return (
    <div className="flex gap-2 flex-wrap">
      {supplier.supplierData.demographicDesignations.map((it) => (
        <Tag icon={<IconAward size={12} />} color="purple" text={it} />
      ))}
    </div>
  );
};

interface SupplierModalProps {
  orgId: string;
  show: boolean;
  close: () => void;
}

export const SupplierProfile = ({ orgId, show, close }: SupplierModalProps) => {
  const { data: supplier, isLoading } = useGetSupplierQuery(orgId);

  const title = (
    <Loading
      loading={isLoading}
      skeleton={
        <div>
          <Skeleton width={200} height={20} my={10} />
          <Skeleton width={150} height={20} />
        </div>
      }
    >
      <div className="flex-col gap-2 flex items-start">
        <span className="text-text-secondary text-xl font-bold">
          {supplier?.orgName}
        </span>
        <div className="flex gap-2">
          <CertificationTags supplier={supplier!} />

          {supplier?.supplierData.currentSRARange && (
            <Tag
              icon={<IconTrophy size={12} />}
              color={
                supplier?.supplierData.currentSRARange?.toLowerCase() ===
                'ready'
                  ? 'green'
                  : 'orange'
              }
              text={`SRA Score: ${supplier?.supplierData.currentSRARange}`}
            />
          )}
        </div>
      </div>
    </Loading>
  );

  const renderProfile = () => {
    return (
      <div className="p-4 space-y-6">
        <div className="flex flex-col gap-4">
          <span className="text-xl font-bold">Basic Information</span>
          <span className="text-base font-bold">Description</span>
          <span>{supplier?.supplierData.description}</span>
        </div>

        {/* growth rate */}
        <AnnualGrowthRate supplier={supplier!} />

        {/* URLs */}
        <URLs supplier={supplier!} />

        {/* Industry */}
        <Industry supplier={supplier!} />

        {/* business activities */}
        <BusinessActivities supplier={supplier!} />

        {/* naics */}
        <NaicsCodes supplier={supplier!} />

        {/* main contact */}
        <MainContact supplier={supplier!} />
      </div>
    );
  };

  const renderCertifications = () => {
    return (
      <div className="p-4 space-y-6 w-full">
        <div className="flex flex-col gap-4">
          <span className="text-xl font-bold">Certifications</span>
        </div>

        {/* certifications */}
        <Certifications supplier={supplier!} />
      </div>
    );
  };

  return (
    <Tabs
      searchParamName="spt"
      tabs={[
        { title: 'Profile Information', render: renderProfile },
        ...(hasCertifications(supplier)
          ? [{ title: 'Certifications', render: renderCertifications }]
          : []),
      ]}
    />
  );
};

export const SupplierProfileModal = ({
  orgId,
  show,
  close,
}: SupplierModalProps) => {
  const { data: supplier, isLoading } = useGetSupplierQuery(orgId);

  const title = (
    <Loading
      loading={isLoading}
      skeleton={
        <div>
          <Skeleton width={200} height={20} my={10} />
          <Skeleton width={150} height={20} />
        </div>
      }
    >
      <div className="flex-col gap-2 flex items-start">
        <span className="text-text-secondary text-xl font-bold">
          {supplier?.orgName}
        </span>
        <div className="flex gap-2">
          <CertificationTags supplier={supplier!} />

          {supplier?.supplierData.currentSRARange && (
            <Tag
              icon={<IconTrophy size={12} />}
              color={
                supplier?.supplierData.currentSRARange?.toLowerCase() ===
                'ready'
                  ? 'green'
                  : 'orange'
              }
              text={`SRA Score: ${supplier?.supplierData.currentSRARange}`}
            />
          )}
        </div>
      </div>
    </Loading>
  );

  const renderProfile = () => {
    return (
      <div className="p-4 space-y-6">
        <div className="flex flex-col gap-4">
          <span className="text-xl font-bold">Basic Information</span>
          <span className="text-base font-bold">Description</span>
          <span>{supplier?.supplierData.description}</span>
        </div>

        {/* growth rate */}
        <AnnualGrowthRate supplier={supplier!} />

        {/* URLs */}
        <URLs supplier={supplier!} />

        {/* Industry */}
        <Industry supplier={supplier!} />

        {/* business activities */}
        <BusinessActivities supplier={supplier!} />

        {/* naics */}
        <NaicsCodes supplier={supplier!} />

        {/* main contact */}
        <MainContact supplier={supplier!} />
      </div>
    );
  };

  const renderCertifications = () => {
    return (
      <div className="p-4 space-y-6 w-full">
        <div className="flex flex-col gap-4">
          <span className="text-xl font-bold">Certifications</span>
        </div>

        {/* certifications */}
        <Certifications supplier={supplier!} />
      </div>
    );
  };

  return (
    <Modal
      title={title}
      icon={<SupplierAvatar supplierId={orgId} size={64} />}
      iconSize={64}
      iconBackground={false}
      iconRounded
      showModal={show}
      handleCloseModal={() => close()}
    >
      <Loading
        loading={isLoading}
        skeleton={
          <div className="py-8 px-4">
            <Skeleton height={50} my={16} />
            <Skeleton height={50} my={16} />

            <Skeleton height={150} my={16} />
            <Skeleton height={250} my={16} />
            <Skeleton height={350} my={16} />

            <Skeleton height={150} my={16} />
          </div>
        }
      >
        <Tabs
          searchParamName="spt"
          tabs={[
            { title: 'Profile Information', render: renderProfile },
            ...(hasCertifications(supplier)
              ? [{ title: 'Certifications', render: renderCertifications }]
              : []),
          ]}
        />
      </Loading>
    </Modal>
  );
};
